/**
 * The floating panel the market-info and outcome-info buttons and the reel
 * more-menu hang off (`.m-popover` / `.rl-menu` in the prototype's modals.css).
 *
 * Portalled to the body rather than positioned inside the row that owns it. The
 * prototype has a `.m-popover-fixed` variant for exactly this reason: an
 * absolutely-positioned panel inside a table row is clipped by the row's
 * `overflow` and stacked under later siblings, and the catalogue's rows are
 * scroll containers. Escaping the row is the only thing that works everywhere.
 *
 * Not MUI's `Popover`: that one is modal — it traps focus, renders a backdrop
 * and locks body scroll. This panel is an aside on a row; MUI's `Popper` gives
 * the positioning without the modality, but positioning is the easy half and
 * bringing it in for that alone would still leave the dismiss behaviour here.
 *
 * Dismissal is `onClose`-only: the component never assumes it owns its own
 * visibility, because the anchor usually toggles it. Clicks on the anchor are
 * deliberately not treated as "outside" — otherwise a toggle button would close
 * it here and reopen it in the same click, and the panel could never be shut.
 *
 * It is reachable from the keyboard and it follows its anchor, neither of which
 * the prototype's rule gives you for free — see the two effects below. The short
 * version: portalling puts the panel outside the tab order, so focus is moved in
 * on open and handed back on close; and `position: fixed` coordinates read once
 * go stale the moment anything scrolls, so they are recomputed rather than
 * captured.
 */
import Box from '@mui/material/Box';
import {
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type FocusEvent,
  type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';
import type { Theme } from '@mui/material/styles';

/** The gap between the anchor and the panel, and the panel and the viewport edge. */
const OFFSET = 6;
const VIEWPORT_MARGIN = 16;

/**
 * Where the panel sits, given its anchor.
 *
 * Hangs below the anchor, which is what the prototype does and what reads right
 * almost everywhere. But the prototype's rule is only `top: calc(100% + 6px)`,
 * and a `position: fixed` panel that overflows the bottom of the viewport cannot
 * be scrolled to — it is simply unreachable. So it flips above and is pinned by
 * its `bottom` instead — but only when the panel genuinely does not fit below,
 * measured against its own height. Flipping on "more room above than below"
 * would turn every anchor past the halfway mark upward while it still had room
 * to spare, and in a catalogue of rows the direction would appear to change
 * arbitrarily part-way down the list. Below stays the default until it cannot be.
 *
 * Horizontally it clamps rather than flips: shifting a wide panel left keeps it
 * beside the thing it describes, where mirroring it would land it somewhere
 * unrelated.
 */
const placePanel = (anchorRect: DOMRect | undefined, width: number, panelHeight: number) => {
  if (!anchorRect) return { top: 0, left: 0 };

  const spaceBelow = window.innerHeight - anchorRect.bottom;
  const spaceAbove = anchorRect.top;
  // Only worth flipping if above is actually the better of the two — a panel
  // taller than the whole viewport fits nowhere, and should stay where the
  // design puts it rather than jumping.
  const flip = spaceBelow < panelHeight + OFFSET && spaceAbove > spaceBelow;

  // Math.max keeps the panel on-screen when the viewport is narrower than it is;
  // without it a narrow window would push the panel off the left instead.
  const left = Math.max(0, Math.min(anchorRect.left, window.innerWidth - width - VIEWPORT_MARGIN));

  return flip
    ? { bottom: `${window.innerHeight - anchorRect.top + OFFSET}px`, left: `${left}px` }
    : { top: `${anchorRect.bottom + OFFSET}px`, left: `${left}px` };
};

interface PopoverProps {
  open: boolean;
  /** The element to hang off. Null while the host's ref has not attached yet. */
  anchorEl: HTMLElement | null;
  /** Names the panel for assistive tech, e.g. "Market details". */
  label: string;
  onClose: () => void;
  children: ReactNode;
  /** Panel width; the prototype draws these at 300px and the reel one at 360px. */
  width?: number;
}

export const Popover = ({
  open,
  anchorEl,
  label,
  onClose,
  children,
  width = 300,
}: PopoverProps) => {
  const panelRef = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState(() => placePanel(undefined, width, 0));

  /**
   * Re-placed on scroll and resize, not read once at render.
   *
   * A `position: fixed` panel whose coordinates were computed when it opened
   * stays glued to the viewport while the row it describes scrolls away — and
   * scrolling dismisses nothing here, so it would simply sit there pointing at
   * the wrong fixture. `capture: true` is what catches the catalogue's own
   * scrolling row containers; scroll events from those do not bubble to
   * `document` on their own.
   *
   * Layout effect rather than effect: this runs before paint, so the panel never
   * shows for a frame at the stale coordinates.
   */
  useLayoutEffect(() => {
    if (!open) return;

    // Measured off the panel itself rather than guessed: its height is content-
    // driven — a two-row market summary and the reel more-menu are nothing like
    // each other — so a constant here would flip the wrong one. On the first
    // pass the panel is already in the DOM (this is a layout effect after the
    // portal mounted), so `offsetHeight` is real by the time it is read.
    const reposition = () =>
      setPosition(
        placePanel(anchorEl?.getBoundingClientRect(), width, panelRef.current?.offsetHeight ?? 0),
      );
    reposition();

    window.addEventListener('scroll', reposition, true);
    window.addEventListener('resize', reposition);
    document.addEventListener('scroll', reposition, true);

    return () => {
      window.removeEventListener('scroll', reposition, true);
      window.removeEventListener('resize', reposition);
      document.removeEventListener('scroll', reposition, true);
    };
  }, [open, anchorEl, width]);

  /**
   * Focus moves into the panel on open and back to the anchor on close.
   *
   * The panel is portalled to the end of `<body>`, so tab order runs from the
   * anchor straight to whatever follows it in the DOM — never into the panel.
   * Without this the reel more-menu's actions would be unreachable from the
   * keyboard entirely, and most screen readers would never announce a
   * `role="dialog"` they cannot enter. Returning focus to the anchor on close
   * keeps the operator's place in a long list instead of dropping them on
   * `<body>`.
   */
  useEffect(() => {
    if (!open) return;

    const returnTo = anchorEl;
    panelRef.current?.focus();

    return () => {
      // Checked against `body`, not against the panel: by the time this cleanup
      // runs React has already unmounted the portal, so `panelRef.current` is
      // null and asking whether it still contains focus always says no. An
      // orphaned `activeElement` — the browser's fallback when the focused
      // element is removed — is the reliable signal that focus was ours to give
      // back. If it sits anywhere else the user has moved it themselves, by
      // tabbing out or clicking something, and taking it back would undo that.
      const orphaned = !document.activeElement || document.activeElement === document.body;
      if (orphaned) returnTo?.focus();
    };
  }, [open, anchorEl]);

  useEffect(() => {
    if (!open) return;

    /**
     * Escape belongs to the innermost layer, so this one consumes it.
     *
     * A popover opened inside a `ModalHost` would otherwise never hear it.
     * React bubbles the keydown through the portal to the Dialog, whose
     * handler stops it before it reaches a bubble-phase listener on
     * `document`. The modal then closes instead of the popover, taking the
     * operator's selection with it. Capturing on `document` runs before
     * React's own listeners on the root, and stopping it there keeps the
     * modal from treating the same key as its own dismissal.
     */
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      event.stopPropagation();
      onClose();
    };

    const onPointerDown = (event: MouseEvent) => {
      const target = event.target as Node;
      if (panelRef.current?.contains(target)) return;
      if (anchorEl?.contains(target)) return;
      onClose();
    };

    document.addEventListener('keydown', onKeyDown, true);
    // `mousedown`, not `click`: a click that starts inside the panel and drifts
    // out — selecting an id to copy, which is most of what these panels are for
    // — would otherwise dismiss it on release.
    document.addEventListener('mousedown', onPointerDown);

    return () => {
      document.removeEventListener('keydown', onKeyDown, true);
      document.removeEventListener('mousedown', onPointerDown);
    };
  }, [open, anchorEl, onClose]);

  if (!open) return null;

  /**
   * Tabbing past the panel's last control closes it rather than leaving it open
   * behind the user. This is an aside on a row, not a modal — trapping focus
   * would be the wrong bargain, so it dismisses on the way out instead.
   */
  const onBlur = (event: FocusEvent<HTMLDivElement>) => {
    const next = event.relatedTarget;
    if (next && (panelRef.current?.contains(next) || anchorEl?.contains(next))) return;
    onClose();
  };

  return createPortal(
    <Box
      ref={panelRef}
      role="dialog"
      aria-label={label}
      // Focusable as a whole, so there is somewhere for focus to land on a panel
      // whose content is all static text.
      tabIndex={-1}
      onBlur={onBlur}
      sx={(theme: Theme) => ({
        position: 'fixed',
        ...position,
        zIndex: theme.zIndex.tooltip,
        width: `${width}px`,
        maxWidth: 'calc(100vw - 32px)',
        borderRadius: `${theme.tokens.radius.insetTile}px`,
        border: `${theme.tokens.border.width} solid ${theme.tokens.color.lineStrong}`,
        backgroundColor: theme.tokens.color.surface,
        boxShadow: theme.tokens.shadow.popover,
        overflow: 'hidden',
      })}
    >
      {children}
    </Box>,
    document.body,
  );
};
